import Konva from "konva";
import useImage from "use-image";
import { KonvaEventObject } from "konva/lib/Node";
import { useRef, useState, useEffect, useCallback, RefObject } from "react";
import { Stage, Layer, Image, Rect, Line, Circle } from "react-konva";
import { Rectangle } from "@/components/ui/canvas";
import { Button } from "@/components/ui/button";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { useCanvasContain } from "./use-canvas-contain";
import { ChevronDownIcon, ClipboardDocumentIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/20/solid";
import { SelectClassificationModal } from "@/features/studies/modals/select-classification-modal";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { useModalStore } from "@/stores/use-modal-store";
import { Region } from "@/api/studio/data/types/region.type";
import { Classification } from "@/api/studio/data/types/classification.type";
import { StudyType } from "@/api/studio/studies/types/study.type";
import { cn } from "@/utils/classes";
import { classColors, classNames } from "@/utils/classification-mapping";
import { getColorNameByHex, sceneColors } from "@/utils/scene-color-mapping";
import { useContextMenu } from "@/features/studies/hooks/use-context-menu";
import { getUniqueLabel } from "@/features/studies/utils/get-unique-label";
import { toast } from "@/components/ui/toast";
import { useSegment } from "@/hooks/use-segment";
import { PanToolIcon } from "@/assets/study-assets/pan-tool-icon";
import { DrawRectangleIcon } from "@/assets/study-assets/draw-rectangle-icon";
import { PolygonToolIcon } from "@/assets/study-assets/polygon-tool-icon.tsx";
import { Polygon } from "./polygon";
import { getPointerPositionInImage as getCanvasPointerPositionInImage, isAxisAlignedRectangle, normalizeRect, rectToPoints } from "./geometry";
import { useCanvasDrawingState } from "./use-canvas-drawing-state";

/* Constants */
const SCALE_BY = 1.1;
const PINCH_ZOOM_SENSITIVITY = 0.01;
const MIN_SCALE = 0.1;
const MAX_SCALE = 20;
const MIN_DRAW_SIZE = 25;
const CLOSE_POLYGON_DISTANCE = 12;

type SelectionValue = Classification | string;

/* Prop Types */
interface StudyCanvasProps {
  imageUrl: string;
  handleRegionSave: (id: string, x: number, y: number, width: number, height: number) => void;
  assetSize: { width: number; height: number };
  projectId: string;
  studyId: string;
  assetRegions: Region[];
  className?: string;
  studyType?: StudyType;
}

/*
 * StudyCanvas component
 * Uses Konva to render the asset and it's detected AOIs
 */
export const StudyCanvas = ({ imageUrl, assetSize, handleRegionSave, projectId, studyId, assetRegions, className, studyType = "asset" }: StudyCanvasProps) => {
  /* Refs */
  const stageRef = useRef<Konva.Stage>(null);
  const isSpaceHeldRef = useRef(false);

  /* State */
  const [scale, setScale] = useState(1);
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const [selectedSelectionValue, setSelectedSelectionValue] = useState<SelectionValue>(studyType === "scene" ? sceneColors[0].hex : "logo");
  const {
    toolMode,
    isDrawing,
    draftRect,
    draftPolygonPoints,
    polygonCursorPoint,
    drawStartRef,
    setIsDrawing,
    setDraftRect,
    setDraftPolygonPoints,
    setPolygonCursorPoint,
    handleToolSwitch,
    cancelActiveDrawing,
  } = useCanvasDrawingState();

  /* Stores */
  const selectedRegion = useEditorStore((state) => state.selectedRegion);
  const { setSelectedRegion, deleteRegion, addRegion, updateRegion } = useEditorStore((state) => state.editorActions);
  const sidePanelDisplay = useEditorStore((state) => state.sidePanelDisplay);
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);
  const { open, close } = useModalStore((state) => state.modalActions);

  /* Custom hooks */
  const { canvasSize, ratio, position, editorRef, setPosition, posRef } = useCanvasContain(assetSize);
  const { contextMenuPos, handleContextMenu, handleCopy, handlePaste, handleDelete } = useContextMenu(editorRef as RefObject<HTMLDivElement>, {
    projectId,
    studyId,
    studyType,
  });
  const { segmentTrackEvent } = useSegment();

  /* Reset zoom/pan back to the initial contained image position */
  const handleZoomReset = useCallback(() => {
    setScale(1);
    setPosition(posRef.current);
    stageRef.current?.position(posRef.current);
  }, [setPosition, posRef]);

  /* Effects */
  useEffect(() => {
    /* Reset zoom and position when image URL changes */
    handleZoomReset();
    if (editorRef.current) editorRef.current.focus();
  }, [imageUrl, handleZoomReset, editorRef]);

  /* Handlers */
  const handleZoomIn = () => {
    setScale(Math.min(MAX_SCALE, scale * SCALE_BY));
  };

  const handleZoomOut = () => {
    setScale(Math.max(MIN_SCALE, scale / SCALE_BY));
  };

  /* Handle wheel/trackpad events: pinch gestures zoom smoothly toward the cursor,
     while two-finger scroll pans the canvas — both work in every tool mode */
  const handleWheel = (e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    const isPinch = e.evt.ctrlKey || e.evt.metaKey;

    if (isPinch) {
      // Pinch-to-zoom or Ctrl+scroll: use continuous scaling based on actual delta
      const pointer = stageRef?.current?.getPointerPosition();
      if (!pointer) return;

      const mousePoint = {
        x: (pointer.x - stageRef.current!.x()) / scale,
        y: (pointer.y - stageRef.current!.y()) / scale,
      };

      const zoom = 1 - e.evt.deltaY * PINCH_ZOOM_SENSITIVITY;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * zoom));

      setScale(newScale);
      setPosition({
        x: pointer.x - mousePoint.x * newScale,
        y: pointer.y - mousePoint.y * newScale,
      });
    } else {
      // Two-finger scroll (no pinch): pan the canvas
      setPosition((prev) => ({
        x: prev.x - e.evt.deltaX,
        y: prev.y - e.evt.deltaY,
      }));
    }
  };

  const handleClassificationSelect = useCallback(
    (value: string) => {
      setSelectedSelectionValue(value);
      close();
    },
    [close],
  );

  /* Modals */
  const openClassificationModal = useCallback(() => {
    open(<SelectClassificationModal onClassificationSelect={handleClassificationSelect} studyType={studyType} />, { className: "w-[340px] max-h-[516px]" });
  }, [open, handleClassificationSelect, studyType]);

  const createRegionWithSelection = useCallback(
    (selection: SelectionValue, points: { x: number; y: number }[]) => {
      // Build a human-friendly label based on study type and ensure it is unique
      const baseLabel = studyType === "scene" ? "Area" : classNames[selection as Classification];
      const existingLabels = sidePanelDisplay.map((r) => r.label);
      const uniqueLabel = getUniqueLabel(baseLabel, existingLabels);

      // Preserve any previously-used priority for a region with the same generated label
      const foundPriority = allAssetRegionData.flatMap((regionData) => regionData.regions || []).find((r) => r.label === uniqueLabel && r.priority !== undefined)?.priority;

      // Region data is stored generically as points regardless of how it was drawn in the UI
      const newRegion = {
        id: crypto.randomUUID(),
        classification: studyType === "scene" ? ("area" as const) : (selection as Classification),
        color: studyType === "scene" ? (selection as string) : undefined,
        points: points,
        label: uniqueLabel,
        priority: foundPriority ?? 0,
      };

      // Track the creation event with the shape metadata relevant to the current study type
      segmentTrackEvent("AOI Added", {
        color: studyType === "scene" ? selection : undefined,
        classification: studyType === "scene" ? undefined : selection,
        location: "canvas",
        studyType: studyType,
        projectId,
        studyId,
      });

      // Persist the region in editor state and surface success feedback to the user
      addRegion(newRegion);
      toast({
        title: "Region Added",
        type: "success",
      });
    },
    [studyType, sidePanelDisplay, allAssetRegionData, addRegion, segmentTrackEvent, projectId, studyId],
  );

  /* Keyboard events */
  const handleCanvasKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.code === "Space" && !isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = true;
        setIsSpaceHeld(true);
        return;
      }

      switch (e.key) {
        case "Delete":
        case "Backspace":
          if (selectedRegion) {
            deleteRegion(selectedRegion);
            setSelectedRegion(null);
          }
          break;
        case "c":
        case "C":
          if ((e.ctrlKey || e.metaKey) && selectedRegion) {
            handleCopy();
          }
          break;
        case "v":
        case "V":
          if (e.ctrlKey || e.metaKey) {
            handlePaste();
          }
          break;
        case "Escape":
          cancelActiveDrawing();
          break;
        default:
          break;
      }
    },
    [selectedRegion, deleteRegion, setSelectedRegion, handleCopy, handlePaste, cancelActiveDrawing],
  );

  /* Release spacebar to end temporary pan and return to the active drawing tool */
  const handleCanvasKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.code === "Space") {
      e.preventDefault();
      isSpaceHeldRef.current = false;
      setIsSpaceHeld(false);
    }
  }, []);

  /* Helpers */
  const [image] = useImage(imageUrl, "use-credentials");

  const getPointerPositionInImage = useCallback(
    (requireInside = false) => {
      // Shared helper converts Konva stage coordinates into image-space region coordinates
      return getCanvasPointerPositionInImage({
        stage: stageRef.current,
        scale,
        canvasSize,
        ratio,
        requireInside,
      });
    },
    [canvasSize.height, canvasSize.width, ratio, scale],
  );

  /* Start a new rectangle draft when the user presses down on the image */
  const handleDrawStart = (e: KonvaEventObject<MouseEvent>) => {
    // Only left-clicks on the stage/image should start a new rectangle
    if (toolMode !== "draw-rect" || e.evt.button === 2) return;
    const targetType = e.target.getClassName();
    if (targetType !== "Stage" && targetType !== "Image") return;

    // The starting point must be inside the image bounds
    const start = getPointerPositionInImage(true);
    if (!start) return;

    // Store the start point and create an empty draft rect for live preview rendering
    drawStartRef.current = start;
    setIsDrawing(true);
    setDraftRect({ x: start.x, y: start.y, width: 0, height: 0 });
  };

  /* Update the in-progress rectangle preview as the pointer moves */
  const handleDrawMove = () => {
    if (!isDrawing || toolMode !== "draw-rect") return;
    const start = drawStartRef.current;
    const end = getPointerPositionInImage();
    if (!start || !end) return;

    // Normalize the rect so width/height stay positive no matter drag direction
    setDraftRect(normalizeRect(start, end));
  };

  /* Finalize the rectangle draft and convert it into a stored points-based region */
  const handleDrawEnd = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    // Read the start and end positions before clearing the active draw ref
    const start = drawStartRef.current;
    const end = getPointerPositionInImage();
    drawStartRef.current = null;

    // If we lost either endpoint, cancel the preview without saving
    if (!start || !end) {
      setDraftRect(null);
      return;
    }

    const rect = normalizeRect(start, end);
    setDraftRect(null);

    // Ignore tiny accidental drags so clicks do not create unusable regions
    if (rect.width < MIN_DRAW_SIZE || rect.height < MIN_DRAW_SIZE) return;

    // Convert the rectangle preview into the generic region points format
    const points = rectToPoints(rect);

    // Switch back to pan tool after drawing the rect
    handleToolSwitch("pan");

    // Create the region using the currently selected classification/color
    createRegionWithSelection(selectedSelectionValue, points);
  };

  const handlePolygonPointClick = (e: KonvaEventObject<MouseEvent>) => {
    // Only allow left clicks for adding points, and only if we're in draw-polygon mode
    if (toolMode !== "draw-polygon" || e.evt.button === 2) return;

    // Prevent the stage from interpreting this as a drag or other action
    const targetType = e.target.getClassName();
    if (targetType !== "Stage" && targetType !== "Image") return;

    // Get the pointer position in the image coordinate space, and ensure it's inside the image
    const point = getPointerPositionInImage(true);
    if (!point) return;

    // If there are at least 3 points and the new point is close to the start, close the polygon
    if (draftPolygonPoints.length >= 3) {
      const closeThreshold = CLOSE_POLYGON_DISTANCE / ratio;
      const distanceToStart = Math.hypot(point.x - draftPolygonPoints[0].x, point.y - draftPolygonPoints[0].y);

      // If the point is close enough to the start, create the region and reset the draft points
      if (distanceToStart <= closeThreshold) {
        createRegionWithSelection(selectedSelectionValue, draftPolygonPoints);
        setDraftPolygonPoints([]);
        setPolygonCursorPoint(null);

        // Switch back to pan tool after drawing the polygon
        handleToolSwitch("pan");
        return;
      }
    }

    setDraftPolygonPoints((currentPoints) => [...currentPoints, point]);
  };

  const handlePolygonMouseMove = () => {
    // Only update the cursor point if we're in the middle of drawing a polygon
    if (toolMode !== "draw-polygon") return;

    // Store the current pointer so the preview line can extend to the cursor
    const point = getPointerPositionInImage();
    setPolygonCursorPoint(point);
  };

  const handlePolygonSave = useCallback(
    (id: string, points: { x: number; y: number }[]) => {
      // Polygon edits are persisted as rounded point coordinates for consistency
      updateRegion(
        id,
        points.map((point) => ({
          x: Math.round(point.x),
          y: Math.round(point.y),
        })),
      );
    },
    [updateRegion],
  );

  /* Consts */
  const polygonPreviewPoints = draftPolygonPoints.length > 0 ? [...draftPolygonPoints, ...(polygonCursorPoint ? [polygonCursorPoint] : [])] : [];
  const isPolygonCloseToStart =
    draftPolygonPoints.length >= 3 && polygonCursorPoint
      ? Math.hypot(polygonCursorPoint.x - draftPolygonPoints[0].x, polygonCursorPoint.y - draftPolygonPoints[0].y) <= CLOSE_POLYGON_DISTANCE / ratio
      : false;

  // These values drive the current drawing chip and the draft-shape preview styling
  const selectedDisplayColor = studyType === "scene" ? (selectedSelectionValue as string) : classColors[selectedSelectionValue as Classification];
  const selectedDisplayLabel = studyType === "scene" ? getColorNameByHex(selectedSelectionValue as string) : classNames[selectedSelectionValue as Classification];

  /* Render */
  return (
    <div
      className={cn(
        "grow flex flex-row relative overflow-hidden h-full outline-none",
        isSpaceHeld ? "cursor-grab" : toolMode !== "pan" ? "cursor-crosshair" : "cursor-grab",
        className,
      )}
      ref={editorRef}
      tabIndex={-1}
      onKeyDown={handleCanvasKeyDown}
      onKeyUp={handleCanvasKeyUp}
      onBlur={() => {
        isSpaceHeldRef.current = false;
        setIsSpaceHeld(false);
      }}
    >
      {/* Controls */}
      <div className="flex flex-row gap-2 absolute right-4 top-4 z-10">
        <CustomDropdown
          className="p-0"
          triggerBtnClassname="p-0"
          align="end"
          triggerBtn={
            <Button>
              {Math.round(scale * 100)}% <ChevronDownIcon className="size-3.5 inline-block" />
            </Button>
          }
        >
          <CustomDropdownItem onClick={handleZoomIn}>Zoom in</CustomDropdownItem>
          <CustomDropdownItem onClick={handleZoomOut}>Zoom out</CustomDropdownItem>
          <CustomDropdownItem onClick={handleZoomReset}>Fit to canvas</CustomDropdownItem>
        </CustomDropdown>
      </div>
      {/* Tools */}
      <div className="absolute flex top-0 left-0 m-4 z-20">
        <div className="flex flex-row gap-1 rounded-lg bg-base-100 p-1 shadow">
          <Button variant="ghost" selected={toolMode === "pan"} onClick={() => handleToolSwitch("pan")} title="Pan">
            <PanToolIcon className={`${toolMode === "pan" ? "text-white" : ""} size-4`} />
          </Button>
          <Button variant="ghost" selected={toolMode === "draw-rect"} onClick={() => handleToolSwitch("draw-rect")} title="Draw rectangle">
            <DrawRectangleIcon className={`${toolMode === "draw-rect" ? "text-white" : ""} size-4`} />
          </Button>
          <Button variant="ghost" selected={toolMode === "draw-polygon"} onClick={() => handleToolSwitch("draw-polygon")} title="Draw polygon">
            <PolygonToolIcon className={`${toolMode === "draw-polygon" ? "text-white" : ""} size-4`} />
          </Button>
        </div>
        <div className="flex flex-row gap-1 rounded-lg bg-base-100 p-1 shadow ml-2.5 group">
          {/* Select drawing classification */}
          <Button
            variant="ghost"
            onClick={openClassificationModal}
            className="group flex items-center gap-2 cursor-pointer transition-all"
            title={selectedDisplayLabel || "Select classification"}
          >
            {selectedDisplayColor && <div className="size-4 rounded-xs inline-block flex-shrink-0" style={{ backgroundColor: selectedDisplayColor }} />}
            {selectedDisplayLabel && (
              <span className="text-sm font-medium max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-500 ease-in-out group-hover:max-w-[120px] group-hover:opacity-100">
                {selectedDisplayLabel}
              </span>
            )}
            <ChevronDownIcon className="size-5 flex-shrink-0" />
          </Button>
        </div>
      </div>
      {/* Stage */}
      <Stage
        ref={stageRef}
        tabIndex={0}
        width={editorRef.current?.clientWidth}
        height={editorRef.current?.clientHeight}
        scale={{ x: scale, y: scale }}
        position={position}
        onWheel={handleWheel}
        draggable={toolMode === "pan" || isSpaceHeld}
        onMouseDown={(e) => {
          if (isSpaceHeldRef.current) return;
          if (toolMode === "draw-rect") handleDrawStart(e);
          if (toolMode === "draw-polygon") handlePolygonPointClick(e);
        }}
        onMouseMove={() => {
          if (isSpaceHeldRef.current) return;
          if (toolMode === "draw-rect") handleDrawMove();
          if (toolMode === "draw-polygon") handlePolygonMouseMove();
        }}
        onDragEnd={() => {
          const stage = stageRef.current;
          if (stage) setPosition({ x: stage.x(), y: stage.y() });
        }}
        onMouseUp={() => {
          if (toolMode === "draw-rect") handleDrawEnd();
        }}
        onMouseLeave={() => {
          if (toolMode === "draw-rect") handleDrawEnd();
          if (toolMode === "draw-polygon") setPolygonCursorPoint(null);
        }}
        onContextMenu={handleContextMenu}
        className="outline-hidden"
      >
        <Layer>
          <Image image={image} width={canvasSize.width} height={canvasSize.height} />
        </Layer>
        <Layer>
          {assetRegions &&
            assetRegions.map((region) => {
              // Use color for scene studies, otherwise use classColors
              const color = region?.color || classColors[region?.classification];

              if (isAxisAlignedRectangle(region?.points)) {
                return (
                  <Rectangle
                    key={region?.id}
                    id={region?.id}
                    points={region?.points}
                    strokeColor={color}
                    backgroundColor={color + "50"}
                    label={region?.label}
                    labelColor={color}
                    isSelected={region?.id === selectedRegion}
                    ratio={ratio}
                    canvasSize={canvasSize}
                    handleRegionSave={handleRegionSave}
                    setSelectedRegion={setSelectedRegion}
                    toolMode={toolMode === "pan" ? "pan" : "draw"}
                    scale={scale}
                  />
                );
              }

              return (
                <Polygon
                  key={region?.id}
                  id={region?.id}
                  points={region?.points}
                  strokeColor={color}
                  backgroundColor={color + "50"}
                  label={region?.label}
                  labelColor={color}
                  isSelected={region?.id === selectedRegion}
                  ratio={ratio}
                  canvasSize={canvasSize}
                  onRegionSave={handlePolygonSave}
                  setSelectedRegion={setSelectedRegion}
                  onContextMenu={handleContextMenu}
                  toolMode={toolMode === "pan" ? "pan" : "draw"}
                />
              );
            })}
          {draftRect && (
            <Rect
              x={draftRect.x * ratio}
              y={draftRect.y * ratio}
              width={draftRect.width * ratio}
              height={draftRect.height * ratio}
              stroke={selectedDisplayColor}
              strokeWidth={2}
              dash={[8, 6]}
              fill={selectedDisplayColor + "50"}
              listening={false}
            />
          )}
          {polygonPreviewPoints.length > 1 && (
            <Line
              points={polygonPreviewPoints.flatMap((point) => [point.x * ratio, point.y * ratio])}
              stroke={selectedDisplayColor}
              strokeWidth={2}
              dash={[8, 6]}
              closed={isPolygonCloseToStart}
              fill={isPolygonCloseToStart ? selectedDisplayColor + "30" : undefined}
              listening={false}
            />
          )}
          {draftPolygonPoints.map((point, index) => (
            <Circle
              key={`draft-point-${index}-${point.x}-${point.y}`}
              x={point.x * ratio}
              y={point.y * ratio}
              radius={index === 0 ? 5 : 4}
              fill={index === 0 && isPolygonCloseToStart ? "white" : selectedDisplayColor}
              stroke={selectedDisplayColor}
              strokeWidth={2}
              listening={false}
            />
          ))}
        </Layer>
      </Stage>
      {/* Context Menu */}
      {contextMenuPos && (
        <div className="absolute z-50 bg-base-100 rounded-2xl shadow-md p-2" style={{ left: contextMenuPos.x, top: contextMenuPos.y }}>
          <Button variant={"ghost"} className="flex" onClick={handleCopy} disabled={!selectedRegion}>
            <DocumentTextIcon className="inline-block size-4 mr-1" />
            Copy
          </Button>
          <Button variant={"ghost"} className="flex" onClick={handlePaste}>
            <ClipboardDocumentIcon className="inline-block size-4 mr-1" />
            Paste
          </Button>
          <Button variant={"ghost"} className="flex text-error" onClick={handleDelete} disabled={!selectedRegion}>
            <TrashIcon className="inline-block size-4 mr-1 text-error" />
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};
